/** Studio Admin → Cowork → one person's session, read-only. The read is recorded, and shows under "Viewed by" on their page. */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { adminClient, type CoworkSessionRead, type CoworkVersion } from '@/api/adminClient';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { LoadError, SkeletonList } from '@/components/ui/load-state';
import { formatDateTime } from '@/lib/format';

import { KIND_LABELS, ORIGIN_LABELS, threadLine } from './coworkModel';

function Version({ version }: { version: CoworkVersion }) {
  const gaps = (version.notFound ?? []).map((item) => item.text).filter(Boolean);
  return (
    <div className="border-t border-border py-3 first:border-t-0">
      <p className="text-sm font-medium text-foreground">v{version.version}: {version.output?.title ?? 'Untitled'}</p>
      {version.output?.summary ? <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{version.output.summary}</p> : null}
      {(version.output?.themes ?? []).map((theme, index) => (
        <div key={index} className="mt-2">
          <p className="text-sm text-foreground">{theme.title}</p>
          {theme.commentary ? <p className="text-sm text-muted-foreground">{theme.commentary}</p> : null}
        </div>
      ))}
      {gaps.length ? (
        <div className="mt-2">
          <p className="text-xs font-medium text-muted-foreground">What we don't have</p>
          <ul className="list-disc pl-5 text-sm text-muted-foreground">
            {gaps.map((gap, index) => <li key={index}>{gap}</li>)}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function CoworkSessionPage() {
  const { sessionId = '' } = useParams();
  const [data, setData] = useState<CoworkSessionRead | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoadError(undefined);
    try {
      setData(await adminClient.getCoworkSession(sessionId));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load this session');
    }
  }, [sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loadError) return <div className={PAGE_CONTAINER_CLASS}><LoadError message={loadError} onRetry={() => void load()} /></div>;
  if (!data) return <div className={PAGE_CONTAINER_CLASS}><SkeletonList rows={6} label="Loading this session" /></div>;

  const { session, thread, versions, misses } = data;
  const lines = thread.flatMap((wire) => threadLine(wire) ?? []);
  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <Link className="text-sm text-muted-foreground underline" to="/cowork">Cowork</Link>
      <AdminPageHeader
        title={session.title || 'Untitled'}
        description={`${session.owner} · ${session.organizationName} · ${formatDateTime(session.createdAt)}. This read is recorded.`}
      />
      <div className="flex flex-col gap-6">
        {misses.length ? (
          <AdminSection title="What they did not get">
            <ul className="flex flex-col gap-2">
              {misses.map((miss) => (
                <li key={miss.id} className="text-sm text-foreground">
                  {miss.text}
                  <span className="text-muted-foreground"> · {KIND_LABELS[miss.kind]} · {ORIGIN_LABELS[miss.origin]} · {miss.status}</span>
                </li>
              ))}
            </ul>
          </AdminSection>
        ) : null}
        <AdminSection title="Thread">
          <ol className="flex flex-col gap-3">
            {lines.map((line) => (
              <li key={line.key}>
                <p className="text-xs text-muted-foreground">{line.who} · {formatDateTime(line.at)}</p>
                <p className="whitespace-pre-wrap text-sm text-foreground">{line.text}</p>
              </li>
            ))}
          </ol>
        </AdminSection>
        {versions.length ? (
          <AdminSection title="Reports">
            {versions.map((version) => <Version key={version.id} version={version} />)}
          </AdminSection>
        ) : null}
      </div>
    </div>
  );
}
